import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { getWalletRecharges, verifyWalletRecharge } from '../api/walletRecharges';
import { useToast } from '../context/ToastContext';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { formatDate, formatCurrency, getErrorMessage } from '../lib/utils';

const PAGE_SIZE = 20;
const FILTERS = ['all', 'created', 'pending', 'completed', 'failed', 'refunded'];

export default function WalletRecharges() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, isLoading } = useQuery({
    queryKey: ['wallet-recharges', page, filter, debouncedSearch],
    queryFn: () =>
      getWalletRecharges({
        page,
        limit: PAGE_SIZE,
        ...(filter !== 'all' ? { status: filter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const recharges = data?.items ?? [];
  const total = data?.total ?? recharges.length;

  const verify = useMutation({
    mutationFn: (id) => verifyWalletRecharge(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['wallet-recharges'] });
      const outcome = res.data?.outcome ?? '';
      if (outcome.startsWith('updated')) {
        addToast(`Status updated from Razorpay: ${outcome.split(':')[1] ?? ''}`, 'success');
      } else if (outcome.startsWith('unchanged')) {
        addToast('Already up to date with Razorpay', 'info');
      } else {
        addToast('Checked Razorpay — no payment attempt found yet', 'info');
      }
    },
    onError: (err) => addToast(getErrorMessage(err, 'Verification failed'), 'error'),
  });

  const statusColor = (s) => {
    if (s === 'completed') return 'success';
    if (s === 'pending' || s === 'created') return 'warning';
    if (s === 'failed') return 'danger';
    if (s === 'refunded') return 'info';
    return 'default';
  };

  const columns = [
    { key: 'id', label: 'ID', render: (v) => <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">#{v}</span> },
    { key: 'driver_id', label: 'Driver', render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    { key: 'amount', label: 'Amount', render: (v) => <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(v)}</span> },
    { key: 'payment_method', label: 'Method', render: (v) => <Badge variant="info">{v ?? '—'}</Badge> },
    { key: 'status', label: 'Status', render: (v) => <Badge variant={statusColor(v)}>{v ?? '—'}</Badge> },
    {
      key: 'order_id', label: 'Razorpay Order / Payment',
      render: (v, row) => (
        <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
          <div title={v || ''}>{v ? `${v.slice(0, 18)}…` : '—'}</div>
          <div title={row.transaction_id || ''}>{row.transaction_id ? `${row.transaction_id.slice(0, 18)}…` : '—'}</div>
        </div>
      ),
    },
    { key: 'confirmed_via', label: 'Confirmed Via', render: (v) => v ? <span className="text-xs text-gray-500">{v}</span> : <span className="text-gray-400">—</span> },
    { key: 'recharged_at', label: 'Created', render: (v) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span> },
    {
      key: 'actions', label: 'Actions',
      render: (_, row) => (
        <Button
          size="xs"
          variant="secondary"
          icon={RefreshCw}
          loading={verify.isPending && verify.variables === row.id}
          onClick={(e) => { e.stopPropagation(); verify.mutate(row.id); }}
        >
          Verify
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Wallet Top-ups</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} records · driver access wallet recharges via Razorpay</p>
        </div>
        <SearchInput
          placeholder="Search driver, order or payment ID…"
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          className="w-full sm:w-80"
        />
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors',
              filter === f
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={recharges}
        loading={isLoading}
        emptyMessage={debouncedSearch ? `No top-ups match “${debouncedSearch}”` : 'No wallet top-ups found'}
      />
      <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
    </div>
  );
}
